import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Check, ArrowRight, Star, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Pricing() {
  const [pricing, setPricing] = useState({ monthly_price: 99, featured_price: 149, featured_annual_price: 1788 });
  const { user } = useAuth();

  useEffect(() => {
    axios.get(`${API}/config/pricing`).then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  const basicFeatures = [
    "Listed in searchable directory",
    "Upload company logo",
    "Upload PDF line card",
    "Unlimited keywords & categories",
    "Direct contact info displayed",
    "Cancel anytime",
  ];

  const featuredFeatures = [
    "Everything in Alliance Member",
    "Homepage featured placement",
    "Priority position in search results",
    "Featured badge on profile",
    "Maximum buyer visibility",
  ];

  return (
    <div className="px-6 sm:px-12 lg:px-24 py-24 max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">// Vendor Pricing</div>
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl text-slate-50">
          Pick your <span className="text-orange-500">tier.</span>
        </h1>
        <p className="mt-4 text-slate-400 max-w-xl mx-auto">Free for buyers always. Vendors pay a flat fee — no commissions, no lead resellers, no per-bid charges.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Buyer plan */}
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">For Buyers</div>
          <h2 className="font-display font-black uppercase tracking-tighter text-3xl text-slate-50 mb-3">Free</h2>
          <p className="text-slate-400 mb-6 text-sm">Search the directory. Download line cards. Contact vendors directly.</p>
          <ul className="space-y-3 mb-8">
            {["Unlimited search", "View profiles", "Download line cards", "Direct contact info", "No account required"].map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                <Check className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" /> {f}
              </li>
            ))}
          </ul>
          <Link to="/search" data-testid="pricing-buyer-cta" className="block text-center py-3 border border-slate-700 text-slate-200 font-bold uppercase tracking-wider hover:border-orange-500 hover:text-orange-500 rounded-sm text-sm">
            Browse Directory
          </Link>
        </div>

        {/* Alliance Member - basic */}
        <div className="bg-slate-900 border-2 border-orange-600 p-6 rounded-sm relative">
          <div className="absolute -top-3 right-4 px-3 py-1 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-wider">Popular</div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">Alliance Member</div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-display font-black text-4xl text-slate-50" data-testid="pricing-basic-amount">${pricing.monthly_price}</span>
            <span className="text-sm text-slate-400 uppercase tracking-wider">/mo</span>
          </div>
          <p className="text-slate-400 mb-6 text-sm">Get listed. Get found.</p>
          <ul className="space-y-3 mb-8">
            {basicFeatures.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-slate-200">
                <Check className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" /> {f}
              </li>
            ))}
          </ul>
          <Link
            to={user ? "/dashboard?tier=basic" : "/register"}
            data-testid="pricing-basic-cta"
            className="flex items-center justify-center gap-2 py-3 bg-orange-600 text-white font-bold uppercase tracking-wider hover:bg-orange-500 rounded-sm text-sm"
          >
            {user ? "Subscribe" : "Get Started"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Featured monthly */}
        <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-2 border-amber-500 p-6 rounded-sm relative">
          <div className="absolute -top-3 right-4 px-3 py-1 bg-amber-500 text-slate-950 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1"><Star className="w-3 h-3 fill-slate-950" /> Featured</div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500 mb-3">Featured Monthly</div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-display font-black text-4xl text-slate-50" data-testid="pricing-featured-amount">${pricing.featured_price}</span>
            <span className="text-sm text-slate-400 uppercase tracking-wider">/mo</span>
          </div>
          <p className="text-slate-400 mb-6 text-sm">Top placement. Maximum visibility.</p>
          <ul className="space-y-3 mb-8">
            {featuredFeatures.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-slate-200">
                <Check className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" /> {f}
              </li>
            ))}
          </ul>
          <Link
            to={user ? "/dashboard?tier=featured" : "/register"}
            data-testid="pricing-featured-cta"
            className="flex items-center justify-center gap-2 py-3 bg-amber-500 text-slate-950 font-bold uppercase tracking-wider hover:bg-amber-400 rounded-sm text-sm"
          >
            {user ? "Subscribe" : "Get Started"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Featured annual */}
        <div className="bg-gradient-to-br from-amber-950/40 via-slate-900 to-slate-950 border-2 border-amber-400 p-6 rounded-sm relative shadow-lg shadow-amber-500/10">
          <div className="absolute -top-3 right-4 px-3 py-1 bg-amber-400 text-slate-950 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Annual</div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-3">Featured Annual</div>
          <div className="flex items-baseline gap-2 mb-6">
            <span className="font-display font-black text-4xl text-slate-50" data-testid="pricing-featured-annual-amount">${pricing.featured_annual_price?.toLocaleString?.() || pricing.featured_annual_price}</span>
            <span className="text-sm text-slate-400 uppercase tracking-wider">/yr</span>
          </div>
          <ul className="space-y-3 mb-8">
            <li className="flex items-start gap-3 text-sm text-amber-100">
              <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" /> <span className="font-semibold">Everything in Featured Monthly</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-amber-100">
              <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" /> <span className="font-semibold">One payment, twelve months of visibility</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-amber-100">
              <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" /> Locked-in rate for the year
            </li>
          </ul>
          <Link
            to={user ? "/dashboard?tier=featured_annual" : "/register"}
            data-testid="pricing-featured-annual-cta"
            className="flex items-center justify-center gap-2 py-3 bg-amber-400 text-slate-950 font-bold uppercase tracking-wider hover:bg-amber-300 rounded-sm text-sm"
          >
            {user ? "Subscribe Yearly" : "Get Started"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
